import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeProviderProps {
    children: React.ReactNode;
    defaultTheme?: Theme;
    storageKey?: string;
}

interface ThemeProviderState {
    theme: Theme;
    resolvedTheme: 'light' | 'dark';
    isDark: boolean;
    setTheme: (theme: Theme) => void;
    toggleTheme: () => void;
}

const initialState: ThemeProviderState = {
    theme: 'dark',
    resolvedTheme: 'dark',
    isDark: true,
    setTheme: () => null,
    toggleTheme: () => null,
};

const ThemeContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
    children,
    defaultTheme = 'light',
    storageKey = 'espacero-theme-v3',
}: ThemeProviderProps) {
    const [theme, setThemeState] = useState<Theme>(() => {
        try {
            const stored = (
                localStorage.getItem(storageKey) ||
                localStorage.getItem('espacero-theme-v3') ||
                localStorage.getItem('espacero-theme-v2') ||
                localStorage.getItem('espacero-ui-theme') ||
                localStorage.getItem('espacero-theme')
            ) as Theme | null;
            return stored || defaultTheme;
        } catch {
            return defaultTheme;
        }
    });

    const getSystemTheme = (): 'light' | 'dark' => {
        if (typeof window === 'undefined') return 'light';
        try {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        } catch {
            return 'light';
        }
    };

    const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
        if (theme === 'system') return getSystemTheme();
        return theme === 'dark' ? 'dark' : 'light';
    });

    const persistTheme = useCallback((val: string) => {
        try {
            localStorage.setItem('espacero-theme-v3', val);
            localStorage.setItem('espacero-theme-v2', val);
            localStorage.setItem('espacero-ui-theme', val);
            localStorage.setItem('espacero-theme', val);
            if (storageKey) localStorage.setItem(storageKey, val);
        } catch {}
    }, [storageKey]);

    useEffect(() => {
        const root = window.document.documentElement;
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const updateTheme = () => {
            const effective = theme === 'system' ? (mediaQuery.matches ? 'dark' : 'light') : theme;
            root.classList.remove('light', 'dark');
            root.classList.add(effective);
            setResolvedTheme(effective);

            const metaTheme = document.querySelector('meta[name="theme-color"]');
            if (metaTheme) {
                metaTheme.setAttribute('content', effective === 'dark' ? '#201913' : '#FAF5EE');
            }
        };

        updateTheme();

        const listener = () => {
            if (theme === 'system') updateTheme();
        };

        mediaQuery.addEventListener?.('change', listener);
        return () => mediaQuery.removeEventListener?.('change', listener);
    }, [theme]);

    const setTheme = useCallback((newTheme: Theme) => {
        persistTheme(newTheme);
        setThemeState(newTheme);
    }, [persistTheme]);

    const toggleTheme = useCallback(() => {
        setThemeState((currentTheme) => {
            let next: Theme;
            if (currentTheme === 'system') {
                const sys = typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
                next = sys === 'dark' ? 'light' : 'dark';
            } else {
                next = currentTheme === 'dark' ? 'light' : 'dark';
            }
            persistTheme(next);
            return next;
        });
    }, [persistTheme]);

    const value = {
        theme,
        resolvedTheme,
        isDark: resolvedTheme === 'dark',
        setTheme,
        toggleTheme,
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
}

export const useTheme = () => {
    const context = useContext(ThemeContext);

    if (context === undefined)
        throw new Error('useTheme must be used within a ThemeProvider');

    return context;
};

